import { useState, useCallback } from 'react';
import { apiService, ServiceRequest, UpdateServiceRequest } from '../services/api';
import type { ServiceDetailsResponse, ServiceListResponse } from '../types/api-responses';
import { getApiErrorMessage } from '../types/api-errors';

export interface UseServicesOptions {
    forOwner?: boolean;
}

export const useServices = (options?: UseServicesOptions) => {
    const forOwner = options?.forOwner ?? false;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [services, setServices] = useState<ServiceDetailsResponse[]>([]);

    const getServices = useCallback(async (searchTerm?: string): Promise<ServiceDetailsResponse[]> => {
        setLoading(true);
        setError(null);
        try {
            const response: ServiceListResponse = forOwner
                ? await apiService.getOwnerServices()
                : await apiService.getStaffServices(searchTerm);
            setServices(response.services);
            return response.services;
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load services. Please refresh or try again later.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [forOwner]);

    const getService = useCallback(async (serviceId: number): Promise<ServiceDetailsResponse> => {
        setLoading(true);
        setError(null);
        try {
            return forOwner
                ? await apiService.getOwnerService(serviceId)
                : await apiService.getStaffServiceById(serviceId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Unable to load service details.');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [forOwner]);

    const createService = useCallback(async (data: ServiceRequest) => {
        if (!forOwner) throw new Error('createService is only available for Owner');
        setLoading(true);
        setError(null);
        try {
            return await apiService.createService(data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to create service');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [forOwner]);

    const updateService = useCallback(async (serviceId: number, data: UpdateServiceRequest): Promise<boolean> => {
        if (!forOwner) throw new Error('updateService is only available for Owner');
        setLoading(true);
        setError(null);
        try {
            return await apiService.updateService(serviceId, data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to update service');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [forOwner]);

    const deleteService = useCallback(async (serviceId: number): Promise<boolean> => {
        if (!forOwner) throw new Error('deleteService is only available for Owner');
        setLoading(true);
        setError(null);
        try {
            return await apiService.deleteService(serviceId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to delete service');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [forOwner]);

    const noopBool = useCallback(async (): Promise<boolean> => {
        throw new Error('Owner only');
    }, []);

    // #51: Optimistically insert a newly-created service so it shows immediately,
    // even if the subsequent list refetch is momentarily stale.
    const prependService = useCallback((service: ServiceDetailsResponse) => {
        setServices(prev => {
            if (prev.some(s => s.id === service.id)) return prev;
            return [service, ...prev];
        });
    }, []);

    return {
        services,
        getServices,
        getService,
        createService: forOwner ? createService : (async () => { throw new Error('Owner only'); }),
        updateService: forOwner ? updateService : noopBool,
        deleteService: forOwner ? deleteService : noopBool,
        prependService,
        loading,
        error
    };
};
